// Klymart — i18n dictionary (EN + DE), aligned with the 2026 pitch deck
export const LOCALES = ['en', 'de'];
export const DEFAULT_LOCALE = 'en';

export const LOCALE_LABELS = {
  en: { short: 'EN', long: 'English' },
  de: { short: 'DE', long: 'Deutsch' },
};

export const messages = {
  en: {
    nav: {
      problem: 'Problem',
      solution: 'Solution',
      howItWorks: 'How it works',
      pop: 'Verified Impact',
      joinWaitlist: 'Get early access',
      home: 'Klymart home',
    },
    hero: {
      headlineA: 'Turn your sustainability advantage',
      headlineB: 'into commercial growth.',
      sub: 'Klymart helps brands prepare for certification, connect with trusted certification partners, manage verified sustainability information, and access financing when needed.',
      ctaPrimary: 'Get early access',
      ctaSecondary: 'See how it works',
      pillars: [
        {
          k: 'We assess',
          t: 'Certification Readiness Assessment',
          v: 'Know which sustainability certifications fit your business.',
        },
        {
          k: 'We connect',
          t: 'Trusted Certification Partners',
          v: 'Find the right certification body for your products.',
        },
        {
          k: 'We finance',
          t: 'Certification Financing',
          v: 'Reduce upfront certification costs through flexible financing.',
        },
        {
          k: 'We verify',
          t: 'Digital Product Passport',
          v: 'Store verified certification data in one digital profile.',
        },
      ],
      privacyNote: 'Your data stays private. No obligation, no spam.',
      tagBottle: 'Verified Impact',
      scanLine: 'Scan to discover the full story',
    },
    stats: {
      items: [
        { k: '$10T+', v: 'Sustainable products market by 2030' },
        { k: '500M+', v: 'SMEs globally underserved' },
        { k: 'One mission', v: 'Build the trust infrastructure of the sustainable economy' },
      ],
    },
    problem: {
      eyebrow: 'The problem',
      heading: 'Sustainability is ',
      headingEm: 'expensive to prove.',
      lead: 'Millions of SMEs invest in sustainable products and responsible supply chains, but they struggle to communicate sustainability compliance in an increasingly complex regulatory and market environment.',
      barriersTitle: 'But they face three major barriers',
      barriers: [
        { tag: 'Expensive', body: 'Sustainability certifications are expensive.' },
        { tag: 'Complex', body: 'Audits and compliance are complex.' },
        { tag: 'Uncertain', body: 'The commercial return is uncertain.' },
      ],
      consumersTitle: 'At the same time',
      consumers: 'Consumers want sustainable products. But they cannot distinguish real impact from greenwashing.',
      vs: { real: 'Real impact. Hard to prove.', wash: 'Greenwashing. Easy to claim.' },
      result: 'Result',
      resultA: 'Good companies remain invisible.',
      resultB: 'Consumers lack trust.',
    },
    solution: {
      eyebrow: 'The solution',
      heading: 'The first platform that ',
      headingEm: 'finances sustainability certification.',
      lead: 'We close the gap between brand ambition and proven impact — across four interlocking steps.',
      flow: [
        { tag: 'Assess', body: 'Evaluate readiness with our smart assessment.' },
        { tag: 'Finance', body: 'We pre-finance certification costs.' },
        { tag: 'Certify', body: 'Get certified with trusted global standards.' },
        { tag: 'Trust', body: 'Build trust and unlock new markets and customers.' },
      ],
      helpsTitle: 'We help brands',
      helps: [
        { t: 'Assess certification readiness', d: 'Understand your sustainability performance.' },
        { t: 'Access certification partners', d: 'Connect with the right certifications and experts.' },
        { t: 'Finance certification costs', d: 'We pre-finance the process, so you can grow.' },
        { t: 'Generate verified sustainability data', d: 'Collect, verify and structure impact data.' },
        { t: 'Build consumer trust', d: 'Make your impact visible, verifiable and valuable.' },
      ],
      tagline: 'We transform certification from a cost into a growth engine.',
      audiencesTitle: 'Who wins',
      audiences: [
        { k: 'For brands', v: 'Lower upfront costs · Faster market access' },
        { k: 'For consumers', v: 'Verified products · Real transparency' },
        { k: 'For the planet', v: 'More impact · Less greenwashing' },
        { k: 'For business', v: 'More trust · More growth' },
      ],
    },
    how: {
      eyebrow: 'How it works',
      heading: 'How Klymart works',
      sub: 'We make sustainability certification simple, affordable and growth-driving.',
      steps: [
        {
          tag: 'Step 01',
          title: 'Certification Readiness Score',
          body: 'We evaluate your sustainability performance across key pillars and surface gaps to close before audit.',
          bullets: ['Traceability', 'Environmental impact', 'Social standards', 'Transparency'],
          mockTitle: 'Klymart Readiness Score',
          mockScore: 72,
          mockHint: 'Good progress',
        },
        {
          tag: 'Step 02',
          title: 'Certification Financing',
          body: 'Klymart pre-finances certification costs so you can start immediately — audits, documentation and administrative fees covered.',
          bullets: ['We cover audits, docs & admin fees', 'You focus on impact. We handle the upfront.'],
          chipA: 'Pre-financed by Klymart',
          chipB: 'No capex hurdle',
        },
        {
          tag: 'Step 03',
          title: 'Revenue Share',
          body: 'Brands repay through a small percentage of future product sales. No large upfront investment required.',
          bullets: ['Aligned incentives', 'Sustainable growth', 'Shared success'],
          metricA: 'Sales impact',
          metricAValue: '+28%',
          metricB: 'Repayment',
          metricBValue: '35% on track',
          metricBSub: 'Thank you for driving real impact.',
        },
      ],
      outcomesTitle: 'Outcomes',
      outcomes: [
        { k: 'Lower upfront costs', v: 'Start your certification journey today.' },
        { k: 'Faster market access', v: 'Get certified and reach more customers.' },
        { k: 'Stronger brand trust', v: 'Verified impact builds loyalty and opens new markets.' },
        { k: 'Scale with impact', v: 'Sustainable by design. Profitable by growth.' },
      ],
    },
    pop: {
      eyebrow: 'Impact Passport',
      heading: 'Verified the moment it matters.',
      sub: 'Every Klymart‑backed brand gets a verifiable trust layer that travels with the product — on shelf, on screen, in the cart.',
      product: {
        name: 'Verdant — Botanical Cleanser',
        meta: 'Refillable · Madagascar origin',
        verifyChip: 'Verified Impact',
        scanCta: 'Scan to verify',
        scanning: 'Scanning…',
        verifying: 'Verifying signature…',
        verified: 'Verified just now',
        verifiedHint: 'Cryptographically signed evidence',
        rescan: 'Rescan',
        tapHint: 'Tap the bottle or button to verify this product',
        checks: [
          { label: 'Origin', value: 'Madagascar' },
          { label: 'Certifications', value: 'Fairtrade · Organic' },
          { label: 'Carbon footprint', value: '−62% vs. industry avg.' },
          { label: 'Social impact', value: 'Fair wages · local communities' },
        ],
      },
      surfaces: ['On‑pack QR', 'E‑comm widget', 'Retail kiosk', 'Marketplace API'],
    },
    waitlist: {
      eyebrow: 'Early partners',
      headingA: 'Be part of the',
      headingB: ' next chapter ',
      headingC: 'of sustainability.',
      lead:
        'We work with a small group of early brand partners before public launch. Tell us about your brand.',
      privacy: 'No obligation. Your information stays private.',
      fields: {
        brandName: 'Brand name',
        contactName: 'Contact name',
        email: 'Work email',
        website: 'Website',
        brandSize: 'Brand size / revenue',
        industry: 'Industry',
        certifications: 'Certifications of interest',
        notes: 'Notes (optional)',
      },
      placeholders: {
        brandName: 'e.g. Verdant Goods',
        contactName: 'e.g. Alex Morgan',
        email: 'alex@verdant.co',
        website: 'verdant.co',
        brandSize: 'Select range',
        industry: 'Select an industry',
        certifications: 'Select certifications',
        notes: 'Tell us about your products, timeline, or current certifications.',
      },
      errors: {
        brandName: 'Brand name is required',
        contactName: 'Contact name is required',
        emailRequired: 'Email is required',
        emailInvalid: 'Please enter a valid email',
        websiteInvalid: 'Please enter a valid URL',
        generic: 'Something went wrong. Please try again.',
      },
      submit: 'Get early access',
      submitting: 'Submitting…',
      selected: 'selected',
      certsSelectedOne: '1 certification selected',
      certsSelectedMany: '{n} certifications selected',
      certsEmpty: 'No certifications listed for this industry.',
      pickIndustryFirst: 'Select an industry first to see relevant certifications.',
      removeCert: 'Remove',
      consent: 'By joining, you agree we may email you about Klymart’s launch. Unsubscribe anytime.',
      successTitle: 'You’re on the list.',
      successBody:
        'Thank you. We’ll reach out when Klymart opens early partnerships. In the meantime, reply to our confirmation with anything else you’d like us to know.',
      successStats: {
        stage: 'Stage', stageValue: 'Early access',
        slots: 'Slots', slotsValue: 'Limited',
        next: 'Next step', nextValue: 'Email',
      },
      toastSuccess: "You're on the list — we'll be in touch.",
    },
    footer: {
      tagline: 'Financing Trust. Accelerating Sustainability.',
      privacy: 'Privacy',
      contact: 'Contact',
      impressum: 'Legal notice',
      terms: 'Terms',
      pitch: 'Pitch deck 2026',
    },
    language: { switch: 'Switch language' },
  },

  de: {
    nav: {
      problem: 'Problem',
      solution: 'Lösung',
      howItWorks: 'So funktioniert’s',
      pop: 'Verifizierte Wirkung',
      joinWaitlist: 'Frühzugang sichern',
      home: 'Klymart Startseite',
    },
    hero: {
      headlineA: 'Aus Nachhaltigkeit',
      headlineB: 'wird kommerzielles Wachstum.',
      sub: 'Klymart unterstützt Marken dabei, sich auf Zertifizierungen vorzubereiten, vertrauenswürdige Zertifizierungspartner zu finden, verifizierte Nachhaltigkeitsdaten zu verwalten und bei Bedarf Finanzierung zu erhalten.',
      ctaPrimary: 'Frühzugang sichern',
      ctaSecondary: 'So funktioniert’s',
      pillars: [
        {
          k: 'Wir prüfen',
          t: 'Readiness‑Check',
          v: 'Erfahren Sie, welche Nachhaltigkeits­zertifizierungen zu Ihrem Geschäft passen.',
        },
        {
          k: 'Wir verbinden',
          t: 'Vertrauensvolle Partner',
          v: 'Finden Sie die passende Zertifizierungsstelle für Ihre Produkte.',
        },
        {
          k: 'Wir finanzieren',
          t: 'Zertifizierungs­finanzierung',
          v: 'Senken Sie anfängliche Zertifizierungskosten durch flexible Finanzierung.',
        },
        {
          k: 'Wir verifizieren',
          t: 'Digitaler Produktpass',
          v: 'Bündeln Sie verifizierte Zertifizierungsdaten in einem digitalen Profil.',
        },
      ],
      privacyNote: 'Ihre Daten bleiben vertraulich. Unverbindlich, kein Spam.',
      tagBottle: 'Verifizierte Wirkung',
      scanLine: 'Scannen für die ganze Geschichte',
    },
    stats: {
      items: [
        { k: '10 Bio.+ €', v: 'Markt für nachhaltige Produkte bis 2030' },
        { k: '500 Mio.+', v: 'KMU weltweit unterversorgt' },
        { k: 'Eine Mission', v: 'Die Vertrauens‑Infrastruktur der nachhaltigen Wirtschaft bauen' },
      ],
    },
    problem: {
      eyebrow: 'Das Problem',
      heading: 'Nachhaltigkeit ist ',
      headingEm: 'teuer nachzuweisen.',
      lead: 'Millionen KMU investieren in nachhaltige Produkte und verantwortungsvolle Lieferketten — doch sie tun sich schwer damit, ihre Nachhaltigkeits‑Compliance in einem zunehmend komplexen regulatorischen und marktwirtschaftlichen Umfeld zu kommunizieren.',
      barriersTitle: 'Doch sie stoßen auf drei Barrieren',
      barriers: [
        { tag: 'Teuer', body: 'Nachhaltigkeitszertifizierungen sind teuer.' },
        { tag: 'Komplex', body: 'Audits und Compliance sind komplex.' },
        { tag: 'Unsicher', body: 'Der kommerzielle Ertrag ist ungewiss.' },
      ],
      consumersTitle: 'Gleichzeitig',
      consumers:
        'Konsument:innen wollen nachhaltige Produkte — können aber echte Wirkung nicht von Greenwashing unterscheiden.',
      vs: { real: 'Echte Wirkung. Schwer zu beweisen.', wash: 'Greenwashing. Leicht zu behaupten.' },
      result: 'Ergebnis',
      resultA: 'Gute Unternehmen bleiben unsichtbar.',
      resultB: 'Konsument:innen fehlt Vertrauen.',
    },
    solution: {
      eyebrow: 'Die Lösung',
      heading: 'Die erste Plattform, die ',
      headingEm: 'Nachhaltigkeits‑Zertifizierungen finanziert.',
      lead:
        'Wir schließen die Lücke zwischen Marken‑Ambition und nachgewiesener Wirkung — in vier ineinandergreifenden Schritten.',
      flow: [
        { tag: 'Assess', body: 'Reife mit unserem smarten Assessment bewerten.' },
        { tag: 'Finance', body: 'Wir finanzieren Zertifizierungskosten vor.' },
        { tag: 'Certify', body: 'Mit anerkannten globalen Standards zertifiziert werden.' },
        { tag: 'Trust', body: 'Vertrauen aufbauen, neue Märkte und Kund:innen erschließen.' },
      ],
      helpsTitle: 'Wir unterstützen Marken',
      helps: [
        { t: 'Zertifizierungsreife bewerten', d: 'Ihre Nachhaltigkeits‑Performance verstehen.' },
        { t: 'Zertifizierungspartner finden', d: 'Mit den richtigen Standards und Expert:innen verbinden.' },
        { t: 'Zertifizierungskosten finanzieren', d: 'Wir finanzieren den Prozess vor — Sie wachsen.' },
        { t: 'Verifizierte Nachhaltigkeitsdaten erzeugen', d: 'Wirkungsdaten erheben, prüfen und strukturieren.' },
        { t: 'Konsumenten‑Vertrauen aufbauen', d: 'Ihre Wirkung sichtbar, prüfbar und wertvoll machen.' },
      ],
      tagline: 'Wir machen aus Zertifizierung statt Kosten einen Wachstumsmotor.',
      audiencesTitle: 'Wer profitiert',
      audiences: [
        { k: 'Für Marken', v: 'Geringere Vorabkosten · Schnellerer Marktzugang' },
        { k: 'Für Konsument:innen', v: 'Verifizierte Produkte · Echte Transparenz' },
        { k: 'Für den Planeten', v: 'Mehr Wirkung · Weniger Greenwashing' },
        { k: 'Fürs Geschäft', v: 'Mehr Vertrauen · Mehr Wachstum' },
      ],
    },
    how: {
      eyebrow: 'So funktioniert’s',
      heading: 'So funktioniert Klymart',
      sub: 'Wir machen Nachhaltigkeits‑Zertifizierung einfach, bezahlbar und wachstumsstark.',
      steps: [
        {
          tag: 'Schritt 01',
          title: 'Zertifizierungs‑Readiness‑Score',
          body: 'Wir bewerten Ihre Nachhaltigkeits‑Performance entlang zentraler Säulen und zeigen Lücken vor dem Audit.',
          bullets: ['Rückverfolgbarkeit', 'Umweltwirkung', 'Soziale Standards', 'Transparenz'],
          mockTitle: 'Klymart Readiness‑Score',
          mockScore: 72,
          mockHint: 'Guter Fortschritt',
        },
        {
          tag: 'Schritt 02',
          title: 'Zertifizierungs‑Finanzierung',
          body: 'Klymart finanziert Zertifizierungskosten vor — Audits, Dokumentation und Verwaltungsgebühren übernehmen wir.',
          bullets: ['Audits, Dokumente & Gebühren übernommen', 'Sie fokussieren Wirkung. Wir das Upfront.'],
          chipA: 'Vorfinanziert durch Klymart',
          chipB: 'Keine Capex‑Hürde',
        },
        {
          tag: 'Schritt 03',
          title: 'Umsatzbeteiligung',
          body: 'Marken zahlen über einen kleinen Anteil zukünftiger Verkäufe zurück. Keine großen Vorabinvestitionen nötig.',
          bullets: ['Ausgerichtete Anreize', 'Nachhaltiges Wachstum', 'Geteilter Erfolg'],
          metricA: 'Umsatz‑Effekt',
          metricAValue: '+28%',
          metricB: 'Rückzahlung',
          metricBValue: '35% im Plan',
          metricBSub: 'Danke, dass Sie echte Wirkung erzeugen.',
        },
      ],
      outcomesTitle: 'Ergebnisse',
      outcomes: [
        { k: 'Geringere Vorabkosten', v: 'Starten Sie Ihre Zertifizierung heute.' },
        { k: 'Schnellerer Marktzugang', v: 'Zertifiziert und näher am Kunden.' },
        { k: 'Stärkeres Markenvertrauen', v: 'Verifizierte Wirkung baut Loyalität und neue Märkte.' },
        { k: 'Mit Wirkung skalieren', v: 'Nachhaltig im Design. Profitabel im Wachstum.' },
      ],
    },
    pop: {
      eyebrow: 'Impact Passport',
      heading: 'Verifiziert im entscheidenden Moment.',
      sub: 'Jede von Klymart unterstützte Marke erhält eine verifizierbare Vertrauensebene, die mit dem Produkt mitwandert — im Regal, am Bildschirm, im Warenkorb.',
      product: {
        name: 'Verdant — Botanical Cleanser',
        meta: 'Nachfüllbar · Madagaskar',
        verifyChip: 'Verifizierte Wirkung',
        scanCta: 'Scannen zum Prüfen',
        scanning: 'Scannen…',
        verifying: 'Signatur wird geprüft…',
        verified: 'Soeben verifiziert',
        verifiedHint: 'Kryptographisch signierte Nachweise',
        rescan: 'Erneut scannen',
        tapHint: 'Tippen Sie auf die Flasche oder den Button, um zu prüfen',
        checks: [
          { label: 'Herkunft', value: 'Madagaskar' },
          { label: 'Zertifizierungen', value: 'Fairtrade · Bio' },
          { label: 'CO₂‑Fußabdruck', value: '−62 % ggü. Branchendurchschnitt' },
          { label: 'Sozialer Impact', value: 'Faire Löhne · lokale Gemeinden' },
        ],
      },
      surfaces: ['On‑Pack QR', 'E‑Commerce Widget', 'Retail Kiosk', 'Marketplace API'],
    },
    waitlist: {
      eyebrow: 'Frühe Partner',
      headingA: 'Werden Sie Teil des',
      headingB: ' nächsten Kapitels ',
      headingC: 'der Nachhaltigkeit.',
      lead:
        'Wir arbeiten vor dem öffentlichen Launch mit einer kleinen Gruppe früher Marken‑Partner. Erzählen Sie uns von Ihrer Marke.',
      privacy: 'Unverbindlich. Ihre Daten bleiben vertraulich.',
      fields: {
        brandName: 'Markenname',
        contactName: 'Ansprechpartner',
        email: 'Geschäftliche E‑Mail',
        website: 'Website',
        brandSize: 'Markengröße / Umsatz',
        industry: 'Branche',
        certifications: 'Zertifizierungen von Interesse',
        notes: 'Notizen (optional)',
      },
      placeholders: {
        brandName: 'z. B. Verdant Goods',
        contactName: 'z. B. Alex Morgan',
        email: 'alex@verdant.co',
        website: 'verdant.co',
        brandSize: 'Bereich wählen',
        industry: 'Branche wählen',
        certifications: 'Zertifizierungen wählen',
        notes: 'Erzählen Sie uns kurz von Ihren Produkten und aktuellen Zertifizierungen.',
      },
      errors: {
        brandName: 'Markenname ist erforderlich',
        contactName: 'Ansprechpartner ist erforderlich',
        emailRequired: 'E‑Mail ist erforderlich',
        emailInvalid: 'Bitte gültige E‑Mail eingeben',
        websiteInvalid: 'Bitte gültige URL eingeben',
        generic: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
      },
      submit: 'Frühzugang sichern',
      submitting: 'Wird gesendet…',
      selected: 'ausgewählt',
      certsSelectedOne: '1 Zertifizierung ausgewählt',
      certsSelectedMany: '{n} Zertifizierungen ausgewählt',
      certsEmpty: 'Keine Zertifizierungen für diese Branche aufgeführt.',
      pickIndustryFirst: 'Wählen Sie zuerst eine Branche, um relevante Zertifizierungen zu sehen.',
      removeCert: 'Entfernen',
      consent:
        'Mit Ihrer Anmeldung stimmen Sie zu, dass wir Sie zum Launch von Klymart per E‑Mail informieren. Jederzeit abbestellbar.',
      successTitle: 'Sie sind auf der Liste.',
      successBody:
        'Vielen Dank. Wir melden uns, sobald Klymart die frühen Partnerschaften öffnet. Antworten Sie gerne auf unsere Bestätigung mit weiteren Informationen.',
      successStats: {
        stage: 'Phase', stageValue: 'Früher Zugang',
        slots: 'Plätze', slotsValue: 'Begrenzt',
        next: 'Nächster Schritt', nextValue: 'E‑Mail',
      },
      toastSuccess: 'Sie sind auf der Liste — wir melden uns.',
    },
    footer: {
      tagline: 'Vertrauen finanzieren. Nachhaltigkeit beschleunigen.',
      privacy: 'Datenschutz',
      contact: 'Kontakt',
      impressum: 'Impressum',
      terms: 'AGB',
      pitch: 'Pitch Deck 2026',
    },
    language: { switch: 'Sprache wechseln' },
  },
};

export const BRAND_SIZES = ['< $1M', '$1M–$10M', '$10M–$50M', '$50M–$200M', '$200M+'];

export const CERTIFICATIONS = {
  en: ['B Corp', 'GOTS', 'Organic', 'Fairtrade', 'Carbon Neutral', 'ISO 14001', 'Other'],
  de: ['B Corp', 'GOTS', 'Bio', 'Fairtrade', 'Klimaneutral', 'ISO 14001', 'Sonstiges'],
};

export const CERT_TO_CANONICAL = {
  en: {
    'B Corp': 'B Corp', GOTS: 'GOTS', Organic: 'Organic', Fairtrade: 'Fairtrade',
    'Carbon Neutral': 'Carbon Neutral', 'ISO 14001': 'ISO 14001', Other: 'Other',
  },
  de: {
    'B Corp': 'B Corp', GOTS: 'GOTS', Bio: 'Organic', Fairtrade: 'Fairtrade',
    Klimaneutral: 'Carbon Neutral', 'ISO 14001': 'ISO 14001', Sonstiges: 'Other',
  },
};

export const CANONICAL_TO_LOCAL = {
  en: Object.fromEntries(Object.entries(CERT_TO_CANONICAL.en).map(([k, v]) => [v, k])),
  de: Object.fromEntries(Object.entries(CERT_TO_CANONICAL.de).map(([k, v]) => [v, k])),
};
